import detectBrowserLanguage from 'detect-browser-language';
import WebViewCommunicatorResource from '../resources/WebViewCommunicatorResource';
import { LANGUAGE_CODES } from '../../resources/lang/languages.generated';

export const LANGUAGE_KEY = 'LANGUAGE';
export const LANGUAGE_PARAM = 'lng';

// The platform-wide fallback: every region lists it last, and untranslated
// languages render English copy until their bundle lands.
export const FALLBACK_LANGUAGE = 'en';

// Locales that actually have a translation bundle under src/resources/lang, from the
// generated registry: add a language to scripts/translate-catalog.config.json and
// re-run the script, never here. A partner's primary language in this list is used
// as-is rather than downgraded to English.
export const BUNDLED_LOCALES = LANGUAGE_CODES;

// What to offer when the brand carries no language set at all — a legacy cache entry or
// an unreachable brand-config. Deliberately NOT BUNDLED_LOCALES: those two meant the
// same thing while only en/de were translated, and offering all nine to a brand we know
// nothing about would be a guess, not a default. English first so such a brand lands on
// the fallback, never on German.
export const UNKNOWN_BRAND_LOCALES = ['en', 'de'];

// The locales a switcher may offer and i18next accepts. Since PROD-476 every one of them
// ships a bundle, so this is the same generated list as BUNDLED_LOCALES; the two names
// stay because callers ask two different questions of it.
export const SUPPORTED_LOCALES = LANGUAGE_CODES;

class LocalizationManager {
  getLanguage() {
    const language = localStorage.getItem(LANGUAGE_KEY);

    return SUPPORTED_LOCALES.includes(language) ? language : null;
  }

  /**
   * Caches the language for this device and tells the POS WebView shell about it.
   *
   * Deliberately does NOT write the staff account: i18next calls this for every
   * language it resolves — the one detected at startup, and the one BrandProvider
   * aligns to when the brand settles. Persisting here recorded a language nobody
   * chose, so a partner's brand language would overwrite the operator's own pick.
   * The account is written where the user actually taps the switcher (see Sidebar).
   */
  saveLanguage(language) {
    localStorage.setItem(LANGUAGE_KEY, language);
    WebViewCommunicatorResource.forwardLanguage({
      lang: language,
    });
  }

  getLanguageFromUrl() {
    const url = window.location.href;
    const query = `${LANGUAGE_PARAM}?=`;

    if (url?.includes(query)) {
      return url?.split(query)[1];
    }

    return null;
  }

  getPreferredLanguage() {
    const preferredBrowserLanguage = detectBrowserLanguage();
    const language = preferredBrowserLanguage?.split(/-|_/)[0];

    return SUPPORTED_LOCALES.includes(language) ? language : null;
  }
}

export default new LocalizationManager();
